import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { MobilePhoneNumberValidator } from '../../../../utils/phone-number';
import { ApiLavandierService } from '../../../../../lib/lib-ngx/web-services/api-lavandier.service';
import { IUserEdit } from '../../../../../lib/lib-ngx/web-services/api-lavandier.service.type';
import { ManageAddressModalComponent } from '../../../modals/manage-address-modal/manage-address-modal.component';
import { ConfirmationModalComponent } from '../../../modals/confirmation-modal/confirmation-modal.component';
import { Router } from '@angular/router';
import { PickupSelectModalComponent } from '../../../modals/pickup-select-modal/pickup-select-modal.component';
import { IHouseDetailsList } from '../../../../../lib/lib-shared/models/House';
import { IAddressContent, IPickup } from '../../../../../lib/lib-shared/api/All.Type';
import { PickupService } from '../../../../services/pickup.service';

@Component({
  selector: 'lm-user-profile-contact-information',
  templateUrl: './user-profile-contact-information.component.html',
  styleUrls: ['./user-profile-contact-information.component.scss',
    '../user-profile.component.scss']
})
export class UserProfileContactInformationComponent implements OnInit {
  @Input('userProfileData') userProfileData;
  @Input('houseList') houseList: IHouseDetailsList[];
  @Output('editValidation') editValidation = new EventEmitter<void>();
  public editUserProfile = false;
  public editForm: FormGroup;
  public userTypeList = [];

  constructor(
    private fb: FormBuilder,
    private apiLavandierService: ApiLavandierService,
    private pickupService: PickupService,
    private modalService: NgbModal,
    private router: Router,
  ) {
  }

  ngOnInit() {
    this.getUserTypeList();
    this.editUserProfile = false;
    this.editForm = this.fb.group({
      lastName: ['', Validators.required],
      firstName: ['', Validators.required],
      phoneNumber: ['', [Validators.required, MobilePhoneNumberValidator()]],
      email: ['', Validators.compose([Validators.required, Validators.email])],
      userType: ['', Validators.required]
    });
  }

  onEditContactInformation() {
    this.editUserProfile = true;
    this.editForm.patchValue({
      lastName: this.userProfileData.lastName,
      firstName: this.userProfileData.firstName,
      phoneNumber: this.userProfileData.phoneNumber,
      email: this.userProfileData.email,
      userType: this.userProfileData.userType.id
    });
  }

  validateEdit() {
    if (!this.editForm.valid) {
      return;
    }
    const number = PhoneNumberUtil.getInstance().parseAndKeepRawInput(this.editForm.value.phoneNumber);
    const userEdit: IUserEdit = {
      userTypeId: this.editForm.value.userType,
      important: this.editForm.value.important,
      dataConsent: this.editForm.value.dataConsent,
      fakePayment: this.editForm.value.fakePayment,
      email: this.editForm.value.email,
      firstName: this.editForm.value.firstName,
      lastName: this.editForm.value.lastName,
      phoneNumber: PhoneNumberUtil.getInstance().format(number, PhoneNumberFormat.INTERNATIONAL),
    };

    this.apiLavandierService.putUser(this.userProfileData.id, userEdit)
      .subscribe(() => {
        this.ngOnInit();
        this.editValidation.emit();
      });
  }

  getUserTypeList() {
    this.apiLavandierService.getUserTypeList({})
      .subscribe((userTypeList: any[]) => this.userTypeList = userTypeList);
  }

  deleteHouse(house: IHouseDetailsList) {
    const confirmationModal = this.modalService.open(ConfirmationModalComponent, {size: 'lg'});
    confirmationModal.componentInstance.confirmationDesc = 'Êtes vous sur de vouloir supprimer ce logement ?';
    confirmationModal.componentInstance.validateButtonText = 'Valider';
    confirmationModal.componentInstance.cancelButtonText = 'Annuler';
    confirmationModal.result.then(result => {
      if (result) {
        this.apiLavandierService.deleteHouseId(house.id, {})
          .subscribe(() => {
            this.ngOnInit();
            this.editValidation.emit();
          });
      }
    }).catch(() => null);
  }

  defaultHouse(house: IHouseDetailsList) {
    this.apiLavandierService.putHouseIdDefault(house.id, {userId: this.userProfileData.id})
      .subscribe(() => {
        this.ngOnInit();
        this.editValidation.emit();
      });
  }

  addHouse(house: IHouseDetailsList) {
    const addAddressModal = this.modalService.open(ManageAddressModalComponent, {size: 'lg'});
    const userData = {
      firstName: this.userProfileData.firstName,
      lastName: this.userProfileData.lastName,
      company: this.userProfileData.company,
      phoneNumber: this.userProfileData.phoneNumber,
      house: house,
    };
    addAddressModal.componentInstance.userData = userData;
    addAddressModal.result.then((result: { atHome: boolean, name: string, shippingAddress: { pickupId: string } & IAddressContent, invoiceAddress?: IAddressContent }) => {
      if (result !== undefined) {
        if (house) {
          this.apiLavandierService.putHouseId(house.id, result).subscribe(() => {
            this.ngOnInit();
            this.editValidation.emit();
          });
        } else {
          this.apiLavandierService.postHouse({
            userId: this.userProfileData.id,
            ...result
          }).subscribe(() => {
            this.ngOnInit();
            this.editValidation.emit();
          });
        }
      }
    })
      .catch(() => null);
  }

  openPickupSelectModal(house: IHouseDetailsList) {
    const modal = this.modalService.open(PickupSelectModalComponent, {size: 'lg'});
    modal.componentInstance.house = house;

    modal.result.then((selectedPickup: IPickup | undefined) => {
      if (!selectedPickup) {
        return;
      }

      const shippingContent: IAddressContent = house.shippingAddress;
      const invoiceContent: IAddressContent = house.invoiceAddress;

      this.apiLavandierService.putHouseId(house.id, {
        atHome: house.atHome,
        name: house.name,
        shippingAddress: {...shippingContent, pickupId: selectedPickup.id},
        ...house.shippingAddressId !== house.invoiceAddressId && {invoiceAddress: invoiceContent},
      }).subscribe(() => {
        this.ngOnInit();
        this.editValidation.emit();
      });
    }).catch(() => null);
  }
}
